import { Controller } from "@hotwired/stimulus"

// A rule is a sentence: when a transaction looks like this, do that. The form
// collects it in pieces, so this reads the pieces back as the sentence they
// make, and says plainly when the rule would do nothing — which is the way
// these go wrong, and is otherwise only discovered by submitting.
//
// It also keeps the property fields out of the way until a property is
// chosen. A unit and a rental treatment mean nothing without one, and shown
// unconditionally they read as general options.
export default class extends Controller {
  static targets = [
    "reading",
    "warning",
    "property",
    "propertyDependent",
    "propertyField",
    "nameContains",
    "account",
    "direction",
    "amountMin",
    "amountMax",
    "category",
    "internal",
    "treatment",
  ]

  declare readonly readingTarget: HTMLElement
  declare readonly warningTarget: HTMLElement
  declare readonly propertyTarget: HTMLSelectElement
  declare readonly propertyDependentTarget: HTMLElement
  declare readonly propertyFieldTargets: HTMLSelectElement[]
  declare readonly nameContainsTarget: HTMLInputElement
  declare readonly accountTarget: HTMLSelectElement
  declare readonly directionTarget: HTMLSelectElement
  declare readonly amountMinTarget: HTMLInputElement
  declare readonly amountMaxTarget: HTMLInputElement
  declare readonly categoryTarget: HTMLSelectElement
  declare readonly internalTarget: HTMLSelectElement
  declare readonly treatmentTarget: HTMLSelectElement

  connect() {
    this.update()
  }

  update() {
    this.toggleProperty()
    this.readingTarget.textContent = `${this.when()}, ${this.then()}`

    const doesNothing = this.actions().length === 0
    this.warningTarget.hidden = !doesNothing
  }

  private toggleProperty() {
    const chosen = this.propertyTarget.value !== ""
    this.propertyDependentTarget.hidden = !chosen
    if (!chosen) this.propertyFieldTargets.forEach((field) => (field.value = ""))
  }

  private when() {
    const phrase = this.nameContains()
      ? `a transaction's name contains ${this.quote(this.nameContains())}`
      : "a transaction arrives"

    const extras: string[] = []
    const account = this.selected(this.accountTarget)
    if (account) extras.push(`in ${account}`)

    const direction = this.directionTarget.value
    if (direction === "in") extras.push("and money is coming in")
    if (direction === "out") extras.push("and money is going out")

    const min = this.amountMinTarget.value.trim()
    const max = this.amountMaxTarget.value.trim()
    if (min && max) extras.push(`for between ${min} and ${max}`)
    else if (min) extras.push(`for at least ${min}`)
    else if (max) extras.push(`for at most ${max}`)

    return `When ${[phrase, ...extras].join(" ")}`
  }

  private then() {
    const actions = this.actions()
    if (actions.length === 0) return "nothing happens yet"

    return actions.join(", and ")
  }

  private actions() {
    const actions: string[] = []

    const category = this.selected(this.categoryTarget)
    if (category) actions.push(`file it under ${category}`)

    const internal = this.internalTarget.value
    if (internal === "true") actions.push("mark it internal, so it stays out of spending")
    if (internal === "false") actions.push("mark it as not internal")

    const property = this.selected(this.propertyTarget)
    if (property) {
      const treatment = this.selected(this.treatmentTarget)
      actions.push(treatment ? `count it as ${treatment.toLowerCase()} for ${property}` : `attribute it to ${property}`)
    }

    return actions
  }

  private nameContains() {
    return this.nameContainsTarget.value.trim()
  }

  // A select's blank option carries real words like "Any account", so an
  // empty value is what marks it unset, never the text shown.
  private selected(field: HTMLSelectElement) {
    if (!field || field.value === "") return null
    return field.options[field.selectedIndex]?.text?.trim() ?? null
  }

  private quote(value: string) {
    return `“${value}”`
  }
}
